import React, { useEffect, useState, forwardRef, useImperativeHandle, useCallback } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import { getImpressoesPorDispositivo } from '../../../../api/reportApi';
import ImpressionsByDeviceChart from '../../pages/Report/ImpressionsByDeviceChart';
import SkeletonCard from './SkeletonCard';
import styles from './ReportCard.module.scss';
import numeral from 'numeral';

interface DeviceReportCardProps {
  campaignId: string;
}

export interface DeviceReportCardRef {
  refresh: () => void;
}

const DeviceReportCard = forwardRef<DeviceReportCardRef, DeviceReportCardProps>(
  ({ campaignId }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeDeviceIndex, setActiveDeviceIndex] = useState<number | null>(null);
    numeral.locale('pt-br');
    const numeralInteger = '0,0';

    const fetchData = useCallback(async () => {
      try {
        setLoading(true);
        setError(null);
        const deviceData = await getImpressoesPorDispositivo(parseInt(campaignId));
        setData(deviceData);
      } catch (err) {
        console.error('Error fetching device data:', err);
        setError('Erro ao carregar dados de dispositivos');
      } finally {
        setLoading(false);
      }
    }, [campaignId]);

    useEffect(() => {
      if (campaignId) {
        fetchData();
      }
    }, [campaignId, fetchData]);

    useImperativeHandle(ref, () => ({
      refresh: fetchData
    }));

    if (loading) {
      return <SkeletonCard rows={2} columns={2} />;
    }

    if (error) {
      return (
        <Card className={`${styles.reportCard} h-full w-full`}>
          <CardHeader className={styles.cardHeader}>
            <div className="flex items-center">
              <div className="bg-primary bg-opacity-10 rounded-full p-2 mr-3">
                <i className="ri-device-line text-primary text-2xl"></i>
              </div>
              <div>
                <h4 className={`${styles.cardTitle} mb-0`}>Dispositivos</h4>
                <small className="text-muted">Distribuição por tipo de dispositivo</small>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center text-destructive">
              <i className="ri-error-warning-line mr-2"></i>
              {error}
            </div>
          </CardContent>
        </Card>
      );
    }

    if (!data || data.length === 0) {
      return (
        <Card className={`${styles.reportCard} h-full w-full`}>
          <CardHeader className={styles.cardHeader}>
            <div className="flex items-center">
              <div className="bg-primary bg-opacity-10 rounded-full p-2 mr-3">
                <i className="ri-device-line text-primary text-2xl"></i>
              </div>
              <div>
                <h4 className={`${styles.cardTitle} mb-0`}>Dispositivos</h4>
                <small className="text-muted">Distribuição por tipo de dispositivo</small>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="text-center text-muted">
              <i className="ri-information-line mr-2"></i>
              Nenhum dado de dispositivo disponível
            </div>
          </CardContent>
        </Card>
      );
    }

    // Device color mapping for consistency
    const getDeviceColor = (device: string) => {
      const deviceLower = device.toLowerCase();
      if (deviceLower.includes('desktop') || deviceLower.includes('pc')) return 'primary';
      if (deviceLower.includes('mobile') || deviceLower.includes('phone')) return 'success';
      if (deviceLower.includes('tablet') || deviceLower.includes('ipad')) return 'warning';
      if (deviceLower.includes('tv') || deviceLower.includes('smart')) return 'info';
      if (deviceLower.includes('watch')) return 'secondary';
      return 'muted';
    };

    const getDeviceIcon = (device: string) => {
      const deviceLower = device.toLowerCase();
      if (deviceLower.includes('desktop') || deviceLower.includes('pc')) return 'ri-computer-line';
      if (deviceLower.includes('mobile') || deviceLower.includes('phone')) return 'ri-smartphone-line';
      if (deviceLower.includes('tablet') || deviceLower.includes('ipad')) return 'ri-tablet-line';
      if (deviceLower.includes('tv') || deviceLower.includes('smart')) return 'ri-tv-line';
      if (deviceLower.includes('watch')) return 'ri-watch-line';
      return 'ri-device-line';
    };

    // Calculate total impressions
    const totalImpressions = data.reduce((sum, item) => sum + ((item.impression || 0) + (item.start || 0)), 0);

    // Hover handlers
    const handleTableRowHover = (index: number | null) => {
      setActiveDeviceIndex(index);
    };

    const handleChartHover = (index: number | null) => {
      setActiveDeviceIndex(index);
    };

    return (
      <Card className={`${styles.reportCard} h-full w-full`}>
        <CardHeader className={styles.cardHeader}>
          <div className="flex items-center">
            <div className="bg-primary bg-opacity-10 rounded-full p-2 mr-3">
              <i className="ri-device-line text-primary text-2xl"></i>
            </div>
            <div>
              <h4 className={`${styles.cardTitle} mb-0`}>Dispositivos</h4>
              <small className="text-muted">Distribuição por tipo de dispositivo</small>
            </div>
            <div className="ml-auto">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <i
                      className="ri-question-line text-muted text-lg cursor-help"
                    ></i>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Distribuição de impressões por tipo de dispositivo (Desktop, Mobile, Tablet, etc.)</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4">
            {/* Chart Section */}
            <div className="text-center">
              {data && (
                <ImpressionsByDeviceChart
                  series={data}
                  activeDeviceIndex={activeDeviceIndex}
                  onHover={handleChartHover}
                />
              )}
            </div>

            {/* Table Section */}
            <div className="table-responsive">
              <table className="table table-borderless table-sm align-middle mb-0">
                <thead className="text-muted">
                  <tr>
                    <th scope="col" className="font-semibold">Dispositivo</th>
                    <th scope="col" className="font-semibold text-end">Impressões</th>
                    <th scope="col" className="font-semibold text-end">Visíveis</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((value, index) => {
                    const device = value.device !== 'null' ? value.device : 'Desconhecido';
                    const deviceIcon = getDeviceIcon(device);
                    const impressions = (value.impression || 0) + (value.start || 0);
                    const viewable = value.viewable || 0;
                    const percentage = totalImpressions > 0 ? ((impressions / totalImpressions) * 100).toFixed(1) : '0.0';
                    if (value.device === 'null' && !impressions && !viewable) {
                      return null;
                    }

                    return (
                      <tr
                        key={index}
                        className={`border-bottom ${activeDeviceIndex === index ? 'table-active' : ''}`}
                        onMouseEnter={() => handleTableRowHover(index)}
                        onMouseLeave={() => handleTableRowHover(null)}
                        style={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                        }}
                      >
                        <td>
                          <div className="flex items-center">
                            <div
                              className="flex items-center justify-center mr-3"
                              style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '50%',
                                backgroundColor: getDeviceColor(device) + '15',
                                border: `2px solid ${getDeviceColor(device)}40`
                              }}
                            >
                              <i
                                className={`${deviceIcon} text-lg`}
                                style={{ color: getDeviceColor(device) }}
                              ></i>
                            </div>
                            <div>
                              <h6 className="mb-0 font-semibold">{device.charAt(0).toUpperCase() + device.slice(1)}</h6>
                              <small className="text-muted">Dispositivo</small>
                            </div>
                          </div>
                        </td>
                        <td className="text-end">
                          <div className="font-bold mr-1">{numeral(impressions).format(numeralInteger)}</div>
                          <Badge variant="secondary" className="bg-primary bg-opacity-10 text-primary px-1 py-1">
                            {percentage}%
                          </Badge>
                        </td>
                        <td className="text-end">
                          <div className="font-bold">{numeral(viewable).format(numeralInteger)}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
);

DeviceReportCard.displayName = 'DeviceReportCard';

export default DeviceReportCard;
